import * as RP from '@radix-ui/react-popover';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { POPOVER_SURFACE } from './Popover.tsx';

export interface EntityHoverCardProps {
  /** The entity reference; must accept a ref and be focusable (a link or button). */
  children: ReactNode;
  /** Rendered only while open, so it can read live data cheaply. */
  content: () => ReactNode;
  /** Hover delay (DesignSystem 3.1: 400 ms); focus opens at once. */
  openDelay?: number;
  closeDelay?: number;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  className?: string;
  /** Off: only the child renders. */
  enabled?: boolean;
}

/**
 * A card of detail and quick actions on any entity reference (machine, session, project,
 * member). Opens on hover after a short delay, or at once on keyboard focus; never steals focus.
 */
export function EntityHoverCard({ children, content, openDelay = 400, closeDelay = 160, side = 'bottom', align = 'start', className, enabled = true }: EntityHoverCardProps) {
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clear = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  const later = (v: boolean, ms: number) => {
    clear();
    timer.current = setTimeout(() => setOpen(v), ms);
  };
  useEffect(() => clear, []);

  if (!enabled) return <>{children}</>;
  return (
    <RP.Root open={open} onOpenChange={setOpen}>
      <RP.Anchor asChild>
        <span
          className="inline-flex min-w-0 max-w-full"
          onPointerEnter={(e) => e.pointerType === 'mouse' && later(true, openDelay)}
          onPointerLeave={(e) => e.pointerType === 'mouse' && later(false, closeDelay)}
          onFocus={(e) => {
            if (e.target.matches(':focus-visible')) {
              clear();
              setOpen(true);
            }
          }}
          onBlur={() => later(false, closeDelay)}
        >
          {children}
        </span>
      </RP.Anchor>
      <RP.Portal>
        <RP.Content
          side={side}
          align={align}
          sideOffset={6}
          collisionPadding={8}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onPointerEnter={clear}
          onPointerLeave={() => later(false, closeDelay)}
          onFocus={clear}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) later(false, closeDelay);
          }}
          className={cn(POPOVER_SURFACE, 'w-72 p-3', className)}
        >
          {open && content()}
        </RP.Content>
      </RP.Portal>
    </RP.Root>
  );
}
